'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

// Comprehensive list of Google Fonts worth trying, organized by style
const FONT_FAMILIES = [
  // --- Sans-Serif ---
  'Inter',
  'Poppins',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Nunito',
  'Raleway',
  'Work Sans',
  'DM Sans',
  'Manrope',
  'Plus Jakarta Sans',
  'Source Sans 3',
  'Outfit',
  'Figtree',
  'Urbanist',
  'Sora',
  'Lexend',
  'Cabin',
  'Karla',
  'Rubik',
  'Quicksand',
  'Mulish',
  'Jost',
  'Barlow',
  'Catamaran',
  'Albert Sans',
  'Red Hat Display',
  'Space Grotesk',
  'Josefin Sans',

  // --- Serif ---
  'Playfair Display',
  'Lora',
  'Merriweather',
  'Libre Baskerville',
  'EB Garamond',
  'Cormorant Garamond',
  'Crimson Text',
  'Spectral',
  'Source Serif 4',
  'Noto Serif',
  'PT Serif',
  'Bitter',
  'Vollkorn',
  'Libre Caslon Text',
  'Cormorant',
  'Cardo',
  'Fraunces',
  'Instrument Serif',
  'DM Serif Display',
  'DM Serif Text',
  'Bodoni Moda',
  'Old Standard TT',
  'Newsreader',
  'Brygada 1918',
  'Sorts Mill Goudy',
  'Literata',
  'Alegreya',
  'Gentium Book Plus',
  'IBM Plex Serif',
  'Zilla Slab',
  'TeX Gyre Pagella',

  // --- Display / Decorative ---
  'Abril Fatface',
  'Bebas Neue',
  'Oswald',
  'Anton',
  'Archivo Black',
  'Alfa Slab One',
  'Righteous',
  'Fredoka',
  'Secular One',
  'Fascinate',
  'Bungee',
  'Lobster',
  'Pacifico',

  // --- Handwriting / Script ---
  'Dancing Script',
  'Great Vibes',
  'Sacramento',
  'Tangerine',
  'Alex Brush',
  'Allura',
  'Pinyon Script',
  'Cormorant Upright',
  'Italianno',

  // --- Monospace ---
  'JetBrains Mono',
  'Fira Code',
  'Source Code Pro',
  'IBM Plex Mono',
  'Space Mono',
];

// Families already loaded locally via next/font — skip Google Fonts fetch for these.
const SELF_HOSTED_FAMILIES = new Set<string>([
  'TeX Gyre Pagella',
]);

const FONT_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

const WEIGHT_LABELS: Record<number, string> = {
  100: 'Thin',
  200: 'ExtraLight',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  800: 'ExtraBold',
  900: 'Black',
};

/**
 * Preload ALL fonts: first load the Google Fonts CSS stylesheets,
 * then trigger actual .woff2 file downloads via document.fonts.load().
 * This eliminates FOUT when cycling through fonts.
 */
function preloadAllFonts() {
  const weights = FONT_WEIGHTS.join(';');
  const chunkSize = 15;
  const linkPromises: Promise<void>[] = [];

  // Step 1: Add CSS stylesheet link tags (Google Fonts only — skip self-hosted)
  const googleFamilies = FONT_FAMILIES.filter(f => !SELF_HOSTED_FAMILIES.has(f));
  for (let i = 0; i < googleFamilies.length; i += chunkSize) {
    const chunk = googleFamilies.slice(i, i + chunkSize);
    const families = chunk
      .map(f => `family=${encodeURIComponent(f)}:wght@${weights}`)
      .join('&');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    linkPromises.push(new Promise<void>((resolve) => {
      link.onload = () => resolve();
      link.onerror = () => resolve(); // resolve anyway so we don't block
    }));
    document.head.appendChild(link);
  }

  // Step 2: Once CSS is loaded, trigger actual font file downloads
  // The CSS only defines @font-face rules; the .woff2 files download
  // on first use. document.fonts.load() forces that download.
  Promise.all(linkPromises).then(() => {
    for (const family of FONT_FAMILIES) {
      for (const weight of FONT_WEIGHTS) {
        document.fonts.load(`${weight} 16px "${family}"`).catch(() => {
          // Font may not support this weight — that's fine
        });
      }
    }
  });
}

function getComputedFontWeight(el: HTMLElement): number {
  return parseInt(window.getComputedStyle(el).fontWeight, 10) || 400;
}

function findClosestWeightIndex(target: number): number {
  let closest = 0;
  let minDiff = Math.abs(FONT_WEIGHTS[0] - target);
  for (let i = 1; i < FONT_WEIGHTS.length; i++) {
    const diff = Math.abs(FONT_WEIGHTS[i] - target);
    if (diff < minDiff) {
      minDiff = diff;
      closest = i;
    }
  }
  return closest;
}

function hasDirectText(el: HTMLElement): boolean {
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim().length > 0) {
      return true;
    }
  }
  return false;
}

function findTextElement(target: HTMLElement): HTMLElement | null {
  if (['IMG', 'SVG', 'VIDEO', 'CANVAS', 'IFRAME'].includes(target.tagName)) {
    return null;
  }

  if (hasDirectText(target)) return target;

  let el: HTMLElement | null = target.parentElement;
  while (el && el.tagName !== 'BODY' && el.tagName !== 'HTML') {
    if (hasDirectText(el)) return el;
    el = el.parentElement;
  }

  if ((target.textContent || '').trim().length > 0) return target;

  return null;
}

export function FontPreviewer() {
  const searchParams = useSearchParams();
  const isActive = searchParams.get('fonts') === 'true';

  const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
  const [familyIndex, setFamilyIndex] = useState(0);
  const [weightIndex, setWeightIndex] = useState(3); // 400 = Regular
  const [elLabel, setElLabel] = useState('');
  const [fontsReady, setFontsReady] = useState(false);

  const stateRef = useRef({ familyIndex, weightIndex, selectedEl });
  stateRef.current = { familyIndex, weightIndex, selectedEl };

  // Preload all fonts on activation
  useEffect(() => {
    if (!isActive || fontsReady) return;
    preloadAllFonts();
    setFontsReady(true);
  }, [isActive, fontsReady]);

  const applyFont = useCallback((el: HTMLElement, fIdx: number, wIdx: number) => {
    const family = FONT_FAMILIES[fIdx];
    const weight = FONT_WEIGHTS[wIdx];
    el.style.setProperty('font-family', `"${family}", sans-serif`, 'important');
    el.style.setProperty('font-weight', String(weight), 'important');
  }, []);

  const describeElement = useCallback((el: HTMLElement): string => {
    const tag = el.tagName.toLowerCase();
    const cls = el.className && typeof el.className === 'string'
      ? el.className.split(' ').find(c => /^(text-|font-|heading|title|hero)/i.test(c)) || ''
      : '';
    const text = (el.textContent || '').slice(0, 50).trim();
    const suffix = (el.textContent || '').length > 50 ? '...' : '';
    const clsSuffix = cls ? ` .${cls}` : '';
    return `<${tag}${clsSuffix}> "${text}${suffix}"`;
  }, []);

  // Kill all pointer/mouse events except on the previewer UI itself.
  // This prevents buttons, links, menus, etc. from firing while in font mode.
  const killEvent = useCallback((e: Event) => {
    if ((e.target as HTMLElement)?.closest?.('[data-font-previewer]')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  }, []);

  const handleClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-font-previewer]')) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const el = findTextElement(target);
    if (!el) return;

    const currentWeight = getComputedFontWeight(el);
    const wIdx = findClosestWeightIndex(currentWeight);

    setSelectedEl(el);
    setFamilyIndex(0);
    setWeightIndex(wIdx);
    setElLabel(describeElement(el));
  }, [describeElement]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const { selectedEl: el, familyIndex: fIdx, weightIndex: wIdx } = stateRef.current;

    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;

    if (!el) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    let newFIdx = fIdx;
    let newWIdx = wIdx;

    switch (e.key) {
      case 'ArrowLeft':
        newFIdx = (fIdx - 1 + FONT_FAMILIES.length) % FONT_FAMILIES.length;
        break;
      case 'ArrowRight':
        newFIdx = (fIdx + 1) % FONT_FAMILIES.length;
        break;
      case 'ArrowUp':
        newWIdx = Math.min(wIdx + 1, FONT_WEIGHTS.length - 1);
        break;
      case 'ArrowDown':
        newWIdx = Math.max(wIdx - 1, 0);
        break;
    }

    if (newFIdx !== fIdx) setFamilyIndex(newFIdx);
    if (newWIdx !== wIdx) setWeightIndex(newWIdx);

    applyFont(el, newFIdx, newWIdx);
  }, [applyFont]);

  useEffect(() => {
    if (!isActive) return;

    // Block all interaction events in capture phase so nothing functional fires.
    // pointerdown/mousedown — stops buttons, drag, focus shifts
    // click — stops navigation, React onClick handlers
    // touchstart — stops mobile taps
    const blockedEvents = ['pointerdown', 'mousedown', 'touchstart'] as const;
    for (const evt of blockedEvents) {
      document.addEventListener(evt, killEvent, true);
    }
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      for (const evt of blockedEvents) {
        document.removeEventListener(evt, killEvent, true);
      }
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isActive, killEvent, handleClick, handleKeyDown]);

  if (!isActive) return null;

  const currentFamily = FONT_FAMILIES[familyIndex];
  const currentWeight = FONT_WEIGHTS[weightIndex];
  const weightLabel = WEIGHT_LABELS[currentWeight];

  return (
    <div
      data-font-previewer
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 99999,
        background: 'rgba(0, 0, 0, 0.88)',
        color: '#fff',
        borderRadius: 10,
        padding: '12px 16px',
        fontSize: 13,
        fontFamily: 'system-ui, sans-serif',
        lineHeight: 1.5,
        maxWidth: 360,
        minWidth: 240,
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        backdropFilter: 'blur(8px)',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa', marginBottom: 6 }}>
        Font Previewer
      </div>

      {selectedEl ? (
        <>
          <div style={{ color: '#888', fontSize: 11, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {elLabel}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 2 }}>
            {currentFamily}
          </div>
          <div style={{ color: '#ccc', fontSize: 12 }}>
            {currentWeight} &middot; {weightLabel}
          </div>
          <div style={{ color: '#666', fontSize: 10, marginTop: 8 }}>
            &larr;&rarr; family &nbsp;&nbsp; &uarr;&darr; weight &nbsp;&nbsp; ({familyIndex + 1}/{FONT_FAMILIES.length})
          </div>
        </>
      ) : (
        <div style={{ color: '#999', fontSize: 12 }}>
          Click any text to start
        </div>
      )}
    </div>
  );
}
